/**
 * Auth Service - Servizi di autenticazione centralizzati --mod
 */

import { publicApi, protectedApi, resetInterceptorState } from './api';
import { API_ENDPOINTS } from '../config/apiConfig';
import { STORAGE_KEYS } from '../utils/constants';
import logger from '../utils/logger';

/**
 * Login utente
 * @param {string} usernameOrEmail - Username o email
 * @param {string} password - Password
 * @returns {Promise<Object>} Dati di login (token, user, etc.)
 */
export const login = async (usernameOrEmail, password) => {
  try {
    logger.log('[authService] Login - chiamata POST /api/Auth/login');

    // IMPORTANTE: Prima del login, pulisci completamente lo stato precedente
    // Questo previene race condition e problemi di cache da sessioni scadute
    logger.log('[authService] Pulizia preventiva dello stato prima del login');
    resetInterceptorState();

    const response = await publicApi.post(API_ENDPOINTS.AUTH.LOGIN, {
      usernameOrEmail,
      password,
    });

    logger.log('[authService] Risposta login:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Login fallito');
    }

    // Salva i token in localStorage
    const { accessToken, refreshToken, expiresAt, user } = data;

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiresAt);

    logger.log('[authService] Login success, ritorno dati con code:', code);
    return { success: true, data, message, code };
  } catch (error) {
    logger.error('[authService] Errore login:', error);
    const errorMessage =
      error.response?.data?.message || error.message || 'Errore durante il login';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Registrazione nuovo utente
 * @param {Object} userData - Dati utente (username, email, nome, password)
 * @returns {Promise<Object>} Risposta registrazione
 */
export const register = async (userData) => {
  try {
    const response = await publicApi.post(API_ENDPOINTS.AUTH.REGISTER, userData);

    const { code, success, message, data } = response.data;

    return { success, message, code, data };
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || error.message || 'Errore durante la registrazione';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Reinvia il codice OTP via email a un utente inattivo.
 * Usato sia dallo step 1 (account inattivo rilevato) che dallo step 2 (codice non ricevuto).
 * @param {number} userId - ID utente
 * @returns {Promise<Object>} { success, message, data: { emailSent } }
 */
export const resendOtp = async (userId) => {
  try {
    const response = await publicApi.post('/api/Auth/resend-otp', { UserId: userId });
    const { success, message, data } = response.data;
    return { success, message, data };
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || error.message || 'Errore durante il reinvio del codice';
    return { success: false, message: errorMessage };
  }
};

/**
 * Invia il codice OTP corrente via SMS al numero di cellulare specificato
 * @param {number} userId - ID utente
 * @param {string} phoneNumber - Numero cellulare italiano (solo cifre, es. "3201234567")
 * @returns {Promise<Object>} { success, message }
 */
export const resendOtpViaSms = async (userId, phoneNumber) => {
  try {
    const response = await publicApi.post('/api/Auth/resend-otp-sms', {
      UserId: userId,
      PhoneNumber: phoneNumber,
    });
    const { success, message, data } = response.data;
    return { success, message, data };
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || error.message || 'Errore durante l\'invio dell\'SMS';
    return { success: false, message: errorMessage };
  }
};

/**
 * Verifica codice OTP inviato via email durante registrazione
 * @param {number} userId - ID utente ricevuto dalla registrazione
 * @param {string} otpCode - Codice OTP a 6 cifre
 * @returns {Promise<Object>} Risposta verifica OTP
 */
export const verifyOtp = async (userId, otpCode) => {
  try {
    logger.log('[authService] Verifica OTP - chiamata POST /api/Auth/verify-otp');
    const response = await publicApi.post(API_ENDPOINTS.AUTH.VERIFY_OTP, {
      UserId: userId,
      OtpCode: otpCode,
    });

    const { code, success, message, data } = response.data;

    logger.log('[authService] Risposta verifica OTP:', { success, code, message });
    return { success, message, code, data };
  } catch (error) {
    logger.error('[authService] Errore verifica OTP:', error);
    const errorMessage =
      error.response?.data?.message || error.message || 'Errore durante la verifica del codice OTP';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Verifica account da email (vecchio flusso con link)
 * @param {string} id - ID utente dal link email
 * @param {string} password - Password scelta dall'utente
 * @returns {Promise<Object>} Risposta verifica
 */
export const verifyAccount = async (id, password) => {
  try {
    const response = await publicApi.post(API_ENDPOINTS.AUTH.VERIFY, {
      id,
      password,
    });

    const { code, success, message, data } = response.data;

    return { success, message, code, data };
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || error.message || 'Errore durante la verifica';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Recupera i dati dell'utente corrente
 * @returns {Promise<Object>} Dati utente
 */
export const getCurrentUser = async () => {
  try {
    logger.log('[authService] ===== INIZIO getCurrentUser =====');
    logger.log('[authService] Chiamata GET /api/Auth/me');

    const response = await protectedApi.get(API_ENDPOINTS.AUTH.ME);

    logger.log('[authService] Risposta /api/Auth/me:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      logger.error('[authService] getCurrentUser fallito - success:', success, 'code:', code);
      throw new Error(message || 'Recupero dati utente fallito');
    }

    // Il backend ora restituisce { user, accessToken, refreshToken, expiresAt, roles }
    // Se c'è un nuovo token, aggiorna l'autenticazione
    if (data.accessToken) {
      logger.log('[authService] Nuovo JWT ricevuto, aggiorno token in localStorage');
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.accessToken);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
      localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, data.expiresAt);
      localStorage.setItem(STORAGE_KEYS.USER_ROLES, data.roles || '');

      // ✅ REFACTORED: Non salva più USER_DATA - AuthContext gestisce la persistenza
      logger.log('[authService] Token salvati in localStorage (USER_DATA gestito da AuthContext)');

      return { success: true, data: data.user, message };
    } else {
      // Fallback per compatibilità con vecchio formato (se il backend non è ancora aggiornato)
      // ✅ REFACTORED: Ritorna solo i dati senza salvarli
      logger.log('[authService] Login completato (formato legacy)');

      return { success: true, data, message };
    }
  } catch (error) {
    logger.error('[authService] ===== ERRORE getCurrentUser =====');
    logger.error('[authService] Tipo errore:', error.constructor.name);
    logger.error('[authService] Messaggio:', error.message);
    logger.error('[authService] Status:', error.response?.status);
    logger.error('[authService] Response data:', error.response?.data);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante il recupero dei dati utente';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    logger.error('[authService] Ritorno errore - success:', success, 'code:', code, 'message:', errorMessage);
    return { success, message: errorMessage, code };
  }
};

/**
 * Pulisce TUTTI i dati dell'app dal localStorage e resetta lo stato dell'applicazione
 * Fix: Rimuove anche eventuali chiavi residue non tracciate e pulisce gli interceptor axios
 */
const clearAllStorage = () => {
  logger.log('[authService] ===== INIZIO PULIZIA COMPLETA APPLICAZIONE =====');

  // 1. Reset stato interceptor axios (richieste in coda, flag refresh, ecc.)
  resetInterceptorState();

  // 2. Rimuovi chiavi conosciute dal localStorage
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  localStorage.removeItem(STORAGE_KEYS.ANAGRAFICA_DATA);

  // 3. Rimuovi tutte le chiavi che iniziano con prefissi dell'app
  const allKeys = Object.keys(localStorage);
  const appPrefixes = ['anutel_', 'auth_', 'user_', 'anagrafica_', 'profile_'];
  // Preferenze UI (guide chiuse) mantenute tra un login e l'altro
  const preservedPrefixes = ['anutel_help_'];

  allKeys.forEach(key => {
    const isAppKey = appPrefixes.some(prefix => key.toLowerCase().startsWith(prefix));
    const isPreserved = preservedPrefixes.some(prefix => key.toLowerCase().startsWith(prefix));
    if (isAppKey && !isPreserved) {
      localStorage.removeItem(key);
      logger.log(`[authService] Rimossa chiave residua: ${key}`);
    }
  });

  logger.log('[authService] localStorage pulito completamente');
  logger.log('[authService] ===== FINE PULIZIA COMPLETA APPLICAZIONE =====');
};

/**
 * Logout utente
 * Chiama l'endpoint protetto /api/Auth/logout e pulisce il localStorage
 * @returns {Promise<Object>} Risposta logout
 */
export const logout = async () => {
  try {
    logger.log('[authService] Logout - chiamata POST /api/Auth/logout');

    // Chiama l'endpoint protetto di logout (POST)
    const response = await protectedApi.post(API_ENDPOINTS.AUTH.LOGOUT);
    logger.log('[authService] Risposta logout:', response.data);

    const { code, success, message } = response.data;

    // Pulisci completamente il localStorage
    clearAllStorage();

    // Trigger evento logout per aggiornare l'AuthContext
    window.dispatchEvent(new Event('auth:logout'));

    logger.log('[authService] Logout completato con successo');
    return { success, message, code };
  } catch (error) {
    logger.error('[authService] Errore durante logout:', error);

    // Anche in caso di errore, pulisci tutti i dati locali
    clearAllStorage();

    // Trigger evento logout
    window.dispatchEvent(new Event('auth:logout'));

    const errorMessage =
      error.response?.data?.message || error.message || 'Errore durante il logout';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Verifica se l'utente è autenticato
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  return !!token;
};

/**
 * Recupera i dati utente dal localStorage
 * @returns {Object|null}
 */
export const getUserDataFromStorage = () => {
  try {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    logger.error('Errore parsing user data:', error);
    return null;
  }
};

/**
 * Refresh token (chiamato automaticamente dall'interceptor)
 * @param {string} refreshToken - Refresh token
 * @returns {Promise<Object>}
 */
export const refreshAccessToken = async (refreshToken) => {
  try {
    const response = await publicApi.post(API_ENDPOINTS.AUTH.REFRESH, {
      refreshToken,
    });

    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Refresh token fallito');
    }

    const { accessToken, refreshToken: newRefreshToken, expiresAt } = data;

    // Salva i nuovi token
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiresAt);

    return { success: true, data, message };
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante il refresh del token';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Recupero password - Step 1: richiesta OTP via email
 * @param {string} email - Email dell'account
 * @returns {Promise<Object>} { success, data: { userId }, message, code }
 */
export const forgotPassword = async (email) => {
  try {
    const response = await publicApi.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
    const { data, code, success, message } = response.data;
    return { success, message, code, data };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Errore durante la richiesta';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;
    return { success, message: errorMessage, code };
  }
};

/**
 * Recupero password - Step 2: verifica OTP
 * @param {number} userId - ID utente
 * @param {string} otpCode - Codice OTP a 6 cifre
 * @returns {Promise<Object>} { success, data: { userId, otpVerified }, message, code }
 */
export const verifyResetOtp = async (userId, otpCode) => {
  try {
    const response = await publicApi.post(API_ENDPOINTS.AUTH.VERIFY_RESET_OTP, {
      UserId: userId,
      OtpCode: otpCode,
    });
    const { data, code, success, message } = response.data;
    return { success, message, code, data };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Errore durante la verifica';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;
    return { success, message: errorMessage, code };
  }
};

/**
 * Recupero password - Step 3: reset password con rivalidazione OTP
 * @param {number} userId - ID utente
 * @param {string} otpCode - Codice OTP (rivalidato lato backend)
 * @param {string} newPassword - Nuova password
 * @returns {Promise<Object>} { success, message, code }
 */
export const resetPassword = async (userId, otpCode, newPassword) => {
  try {
    const response = await publicApi.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      UserId: userId,
      OtpCode: otpCode,
      NewPassword: newPassword,
    });
    const { data, code, success, message } = response.data;
    return { success, message, code, data };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Errore durante il reset';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;
    return { success, message: errorMessage, code };
  }
};

/**
 * Cambio password
 * @param {string} currentPassword - Password attuale
 * @param {string} newPassword - Nuova password
 * @returns {Promise<Object>} Risposta cambio password
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    console.log('[authService] Cambio password - chiamata POST /api/Auth/password');
    const response = await protectedApi.post(API_ENDPOINTS.AUTH.PASSWORD, {
      currentPassword,
      newPassword,
    });

    console.log('[authService] Risposta cambio password:', response.data);
    const { data, code, success, message } = response.data;

    return { success, message, code, data };
  } catch (error) {
    console.error('[authService] Errore cambio password:', error);
    const errorMessage =
      error.response?.data?.message || error.message || 'Errore durante il cambio password';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Aggiorna dati utente (username, email, name)
 * @param {Object} userData - Dati utente da aggiornare
 * @param {string} userData.username - Username
 * @param {string} userData.email - Email
 * @param {string} userData.name - Nome completo
 * @returns {Promise<Object>} Risposta aggiornamento
 */
export const updateUser = async (userData) => {
  try {
    console.log('[authService] Aggiornamento utente - chiamata PUT /api/Auth/user');
    const response = await protectedApi.put(API_ENDPOINTS.AUTH.USER, userData);

    console.log('[authService] Risposta aggiornamento utente:', response.data);
    const { data, code, success, message } = response.data;

    // ✅ REFACTORED: Ritorna solo i dati, senza side-effects su localStorage
    // Il componente chiamante deve usare updateUser() dal context per aggiornare lo stato
    if (success && code === 200) {
      console.log('[authService] Dati utente aggiornati con successo');
    }

    return { success, message, code, data };
  } catch (error) {
    console.error('[authService] Errore aggiornamento utente:', error);
    const errorMessage =
      error.response?.data?.message || error.message || 'Errore durante l\'aggiornamento';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Upload avatar utente
 * @param {File} file - File immagine avatar
 * @returns {Promise<Object>} Risposta upload con avatarPath
 */
export const uploadAvatar = async (file) => {
  try {
    console.log('[authService] Upload avatar - chiamata POST /api/Auth/avatar');

    const formData = new FormData();
    formData.append('file', file);

    const response = await protectedApi.post(API_ENDPOINTS.AUTH.AVATAR, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('[authService] Risposta upload avatar:', response.data);
    const { data, code, success, message } = response.data;

    // ✅ REFACTORED: Ritorna solo i dati, senza side-effects su localStorage
    // Il componente chiamante deve chiamare updateUser() dal context per aggiornare lo stato
    if (success && code === 200 && data?.avatarPath) {
      console.log('[authService] Avatar caricato con successo:', data.avatarPath);
    }

    return { success, message, code, data };
  } catch (error) {
    console.error('[authService] Errore upload avatar:', error);
    const errorMessage =
      error.response?.data?.message || error.message || 'Errore durante l\'upload dell\'avatar';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Recupera la lista di tutti gli utenti del sistema
 * @returns {Promise<Object>} Response con format {success, code, message, data: [{id_utente_sito, id_anagrafico, username, email, nome, is_active, creation_date, roles}], timestamp}
 */
export const getUsers = async () => {
  try {
    console.log('[authService] Recupero lista utenti');

    const response = await protectedApi.get(API_ENDPOINTS.AUTH.GET_USERS);

    console.log('[authService] Risposta lista utenti:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Recupero utenti fallito');
    }

    return { success: true, data, message, code };
  } catch (error) {
    console.error('[authService] Errore recupero utenti:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante il recupero degli utenti';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * ✅ FASE 3: Recupera la lista di tutti gli utenti CON impieghi attivi dal CRM (Admin only)
 * Estende getUsers() aggiungendo array impieghi per ogni utente
 * @returns {Promise<Object>} Response con format {success, code, message, data: [{...user, impieghi: [{id, id_ente, ente, ruolo, ufficio, tipo_impiego, data_inizio, data_fine, stato_ente}]}], timestamp}
 */
export const getUsersWithEmployment = async () => {
  try {
    console.log('[authService] Recupero lista utenti con impieghi (FASE 3)');

    const response = await protectedApi.get('/api/Auth/admin/users-with-employment');

    console.log('[authService] Risposta lista utenti con impieghi:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Recupero utenti con impieghi fallito');
    }

    console.log(`[authService] ✅ Recuperati ${data?.length || 0} utenti con impieghi`);
    return { success: true, data, message, code };
  } catch (error) {
    console.error('[authService] Errore recupero utenti con impieghi:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante il recupero degli utenti con impieghi';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * ✅ FASE 3: Aggiornamento massivo di permessi e ruoli per più utenti (Admin only)
 * Applica le stesse modifiche a tutti gli utenti selezionati
 */
export const batchUpdateUsers = async (userIds, isActive, roles) => {
  try {
    console.log('[authService] Aggiornamento massivo per', userIds.length, 'utenti');

    const requestData = {
      user_ids: userIds,
      is_active: isActive !== null ? (isActive ? 1 : 0) : null,
      roles: roles // Array di {id, description}
    };

    console.log('[authService] Request batch update:', requestData);

    const response = await protectedApi.post('/api/Auth/admin/batch-update', requestData);

    console.log('[authService] Risposta batch update:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Aggiornamento massivo fallito');
    }

    console.log(`[authService] ✅ Aggiornamento massivo completato: ${data.successCount}/${data.totalCount} utenti`);
    return { success: true, data, message, code };
  } catch (error) {
    console.error('[authService] Errore aggiornamento massivo:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante aggiornamento massivo';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Recupera la lista dei ruoli disponibili per l'assegnazione
 * @returns {Promise<Object>} Response con format {success, code, message, data: {roles: [{id, description}]}, timestamp}
 */
export const getRoles = async () => {
  try {
    console.log('[authService] Recupero lista ruoli disponibili');

    const response = await protectedApi.get(API_ENDPOINTS.AUTH.GET_ROLES);

    console.log('[authService] Risposta lista ruoli:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Recupero ruoli fallito');
    }

    return { success: true, data, message, code };
  } catch (error) {
    console.error('[authService] Errore recupero ruoli:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante il recupero dei ruoli';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Aggiorna i ruoli e permessi di un utente
 * @param {Object} roleData - Dati dei ruoli da aggiornare
 * @param {number} roleData.id_utente_sito - ID utente da aggiornare
 * @param {boolean|number} roleData.is_active - Flag utente attivo (0 o 1, oppure true/false)
 * @param {Array} roleData.roles - Array di ruoli da assegnare [{id, description}, ...]
 * @returns {Promise<Object>} Response con format {success, code, message, data: {id_utente_sito}, timestamp}
 */
export const setRole = async (roleData) => {
  try {
    console.log('[authService] Aggiornamento ruoli utente:', roleData);

    const response = await protectedApi.post(API_ENDPOINTS.AUTH.SET_ROLE, roleData);

    console.log('[authService] Risposta aggiornamento ruoli:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Aggiornamento ruoli fallito');
    }

    return { success: true, data, message, code };
  } catch (error) {
    console.error('[authService] Errore aggiornamento ruoli:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante l\'aggiornamento dei ruoli';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Elimina un singolo utente dal sistema (t_users + r_role_user in cascade)
 * @param {number} userId - ID utente da eliminare
 * @returns {Promise<Object>} Response con format {success, code, message}
 */
export const deleteUser = async (userId) => {
  try {
    console.log('[authService] Eliminazione utente:', userId);

    const response = await protectedApi.delete(`${API_ENDPOINTS.AUTH.DELETE_USER}/${userId}`);

    console.log('[authService] Risposta eliminazione utente:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Eliminazione utente fallita');
    }

    console.log(`[authService] ✅ Utente ${userId} eliminato con successo`);
    return { success: true, data, message, code };
  } catch (error) {
    console.error('[authService] Errore eliminazione utente:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Errore durante l'eliminazione dell'utente";
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Elimina più utenti dal sistema in modo massivo (t_users + r_role_user in cascade)
 * @param {number[]} userIds - Array di ID utenti da eliminare
 * @returns {Promise<Object>} Response con format {success, code, message, data: {successCount, totalCount}}
 */
export const batchDeleteUsers = async (userIds) => {
  try {
    console.log('[authService] Eliminazione massiva utenti:', userIds);

    const response = await protectedApi.post(API_ENDPOINTS.AUTH.BATCH_DELETE_USERS, {
      user_ids: userIds,
    });

    console.log('[authService] Risposta eliminazione massiva:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Eliminazione massiva fallita');
    }

    console.log(`[authService] ✅ Eliminazione massiva completata: ${data?.successCount}/${data?.totalCount} utenti`);
    return { success: true, data, message, code };
  } catch (error) {
    console.error('[authService] Errore eliminazione massiva:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Errore durante l'eliminazione massiva degli utenti";
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};

/**
 * Recupera il numero di utenti attualmente online (con sessione attiva)
 * @returns {Promise<Object>} Response con format {success, code, message, data: {count}, timestamp}
 */
export const getOnlineUsers = async () => {
  try {
    console.log('[authService] Recupero conteggio utenti online');

    const response = await publicApi.get(API_ENDPOINTS.AUTH.ONLINE_USERS);

    console.log('[authService] Risposta utenti online:', response.data);
    const { data, code, success, message } = response.data;

    if (!success || code !== 200) {
      throw new Error(message || 'Recupero utenti online fallito');
    }

    return { success: true, data, message, code };
  } catch (error) {
    console.error('[authService] Errore recupero utenti online:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Errore durante il recupero degli utenti online';
    const code = error.response?.data?.code || error.response?.status || 500;
    const success = error.response?.data?.success || false;

    return { success, message: errorMessage, code };
  }
};
